import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Lock,
  LockOpen,
  MessageSquare,
  Pin,
  Search,
  Star,
  Trash2,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { api } from '@/lib/api'
import type { AdminDiscussion, DiscussionCategory, DiscussionStatus } from '@/types'

const categoryLabels: Record<DiscussionCategory, string> = {
  general: '综合讨论',
  book_recommendation: '图书推荐',
  question: '提问求助',
  feedback: '意见反馈',
  other: '其他',
}

const statusLabels: Record<DiscussionStatus, { label: string; color: string }> = {
  active: { label: '活跃', color: 'bg-green-100 text-green-800' },
  closed: { label: '已关闭', color: 'bg-yellow-100 text-yellow-800' },
  hidden: { label: '已隐藏', color: 'bg-gray-200 text-gray-600' },
}

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return String(err)
}

function formatTime(value?: string | null): string {
  if (!value) return '-'
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true, locale: zhCN })
  } catch {
    return value
  }
}

export function DiscussionsPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<AdminDiscussion | null>(null)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message })
    setTimeout(() => setFeedback(null), 3000)
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-discussions'] })

  const discussionsQuery = useQuery({
    queryKey: ['admin-discussions', page, statusFilter, categoryFilter, search],
    queryFn: () =>
      api.getAdminDiscussions({
        page,
        size: 20,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        search: search || undefined,
      }),
  })

  const pinMutation = useMutation({
    mutationFn: (id: string) => api.togglePinDiscussion(id),
    onSuccess: (data) => {
      invalidate()
      showFeedback('success', data.isPinned ? '已设为精华' : '已取消精华')
    },
    onError: (err) => showFeedback('error', getErrorMessage(err)),
  })

  const topMutation = useMutation({
    mutationFn: (id: string) => api.toggleTopDiscussion(id),
    onSuccess: (data) => {
      invalidate()
      showFeedback('success', data.isTop ? '已置顶该讨论' : '已取消置顶')
    },
    onError: (err) => showFeedback('error', getErrorMessage(err)),
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DiscussionStatus }) =>
      api.updateDiscussionStatus(id, status),
    onSuccess: () => {
      invalidate()
      showFeedback('success', '状态已更新')
    },
    onError: (err) => showFeedback('error', getErrorMessage(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDiscussion(id),
    onSuccess: () => {
      invalidate()
      setDeleting(null)
      showFeedback('success', '讨论已删除')
    },
    onError: (err) => showFeedback('error', getErrorMessage(err)),
  })

  const handleSearch = () => {
    setPage(1)
    setSearch(searchInput.trim())
  }

  const items = discussionsQuery.data?.items ?? []
  const pageInfo = discussionsQuery.data?.page

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
              <MessageSquare className="h-4 w-4" /> 讨论管理中心
            </div>
            <h1 className="text-3xl font-bold lg:text-4xl">讨论管理</h1>
            <p className="max-w-3xl text-sm text-white/90 lg:text-base">
              管理社区讨论：置顶重要话题、关闭或删除违规内容
            </p>
          </div>
          <div className="rounded-2xl bg-white/15 p-6 text-center backdrop-blur-md">
            <p className="text-3xl font-bold">{pageInfo?.total ?? '-'}</p>
            <p className="mt-1 text-sm text-white/80">讨论总数</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-gray-100 shadow-xl">
        <CardHeader>
          <CardTitle className="text-lg font-bold text-gray-900">筛选器</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm"
            >
              <option value="">所有状态</option>
              <option value="active">活跃</option>
              <option value="closed">已关闭</option>
              <option value="hidden">已隐藏</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value)
                setPage(1)
              }}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm"
            >
              <option value="">所有分类</option>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="搜索标题或内容..."
                className="w-64"
              />
              <Button variant="secondary" size="sm" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {discussionsQuery.isLoading ? (
          <div className="flex h-48 items-center justify-center text-gray-500">加载中...</div>
        ) : items.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-gray-500">暂无讨论</div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">讨论</th>
                <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">作者</th>
                <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">分类</th>
                <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">状态</th>
                <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">回复</th>
                <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">最后活动</th>
                <th className="px-6 py-4 text-right text-xs font-medium uppercase tracking-wider text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((d) => {
                const statusStyle = statusLabels[d.status] ?? statusLabels.active
                return (
                  <tr key={d.id} className="transition-colors hover:bg-gray-50">
                    <td className="max-w-md px-6 py-4">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            {d.isTop && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                <Pin className="h-3 w-3" />置顶
                              </span>
                            )}
                            {d.isPinned && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                                <Star className="h-3 w-3" />精华
                              </span>
                            )}
                            <p className="truncate text-sm font-medium text-gray-900">{d.title}</p>
                          </div>
                          <p className="mt-1 truncate text-sm text-gray-500">{d.content}</p>
                          {d.minLevel !== null && d.minLevel !== undefined && (
                            <p className="mt-1 text-xs text-blue-600">仅等级 &le; {d.minLevel} 可见</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                      {d.displayName || d.userName || '-'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                        {categoryLabels[d.category] ?? d.category}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyle.color}`}>
                        {statusStyle.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{d.replyCount}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {formatTime(d.lastReplyAt || d.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => topMutation.mutate(d.id)}
                          disabled={topMutation.isPending}
                          title={d.isTop ? '取消置顶' : '置顶（列表最前，App 显示红色"置顶"标签）'}
                          className={`rounded-md p-2 transition-colors hover:bg-red-50 ${
                            d.isTop ? 'text-red-600' : 'text-gray-400 hover:text-red-600'
                          }`}
                        >
                          <Pin className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => pinMutation.mutate(d.id)}
                          disabled={pinMutation.isPending}
                          title={d.isPinned ? '取消精华' : '设为精华（App 显示紫色"精华"标签）'}
                          className={`rounded-md p-2 transition-colors hover:bg-purple-50 ${
                            d.isPinned ? 'text-purple-600' : 'text-gray-400 hover:text-purple-600'
                          }`}
                        >
                          <Star className="h-4 w-4" />
                        </button>
                        {d.status !== 'hidden' && (
                          <button
                            onClick={() =>
                              statusMutation.mutate({
                                id: d.id,
                                status: d.status === 'active' ? 'closed' : 'active',
                              })
                            }
                            disabled={statusMutation.isPending}
                            title={d.status === 'active' ? '关闭讨论（禁止新回复）' : '重新开启讨论'}
                            className="rounded-md p-2 text-gray-400 transition-colors hover:bg-yellow-50 hover:text-yellow-600"
                          >
                            {d.status === 'active' ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                          </button>
                        )}
                        {d.status === 'hidden' ? (
                          <button
                            onClick={() => statusMutation.mutate({ id: d.id, status: 'active' })}
                            disabled={statusMutation.isPending}
                            title="恢复讨论"
                            className="rounded-md p-2 text-gray-400 transition-colors hover:bg-green-50 hover:text-green-600"
                          >
                            <LockOpen className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setDeleting(d)}
                            title="删除讨论"
                            className="rounded-md p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pageInfo && pageInfo.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            共 {pageInfo.total} 条 · 第 {pageInfo.page}/{pageInfo.totalPages} 页
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              上一页
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= pageInfo.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}

      {/* Feedback toast */}
      {feedback && (
        <div
          className={`fixed right-4 top-4 z-50 rounded-lg p-4 shadow-lg animate-fade-in ${
            feedback.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
          }`}
        >
          {feedback.message}
        </div>
      )}

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
            <h2 className="mb-4 text-xl font-bold text-red-600">确认删除讨论</h2>
            <p className="mb-6 text-gray-700">
              确定要删除讨论 "<strong>{deleting.title}</strong>" 吗？将从数据库彻底删除该讨论及其全部回复，<strong className="text-red-600">此操作不可恢复</strong>。
            </p>
            <div className="flex gap-2">
              <Button
                variant="danger"
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleting.id)}
              >
                确认删除
              </Button>
              <Button variant="secondary" onClick={() => setDeleting(null)}>
                取消
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
